import {
  ENTITY_ACCESS_RIGHTS,
  HTTP_STATUS_CODE,
  QUERY_STATUS,
} from "constants/constants";
import _ from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  createSubcontractorCompanyQuery,
  getSubcontractorCompanyQuery,
  updateSubcontractorCompanyQuery,
  searchSubcontractorCompanyByNameQuery,
  getSubcontractorCompanyByIdArrayQuery,
  deleteSubcontractorCompanyQuery,
  getSubcontractorCompanyBySlugQuery,
  getSubcontractorCompaniesByFiltersQuery,
  generateSubcontractorListCSVQuery,
} from "./queries";
import { canUserPerformAction } from "access-roles/general-functions";
import { useUserContext } from "api/user/UserProvider";
import { SUBCONTRACTOR_COMPANY_ROLES } from "access-roles/roles";
import { streamingDownload } from "utils/streamingDownload";
import { extractApiFieldErrorsFlat } from "utils/apiError";

const SubcontractorCompanyContext = createContext({});

export const useSubcontractorCompanyContext = () =>
  useContext(SubcontractorCompanyContext);

export const SubcontractorCompanyProvider = ({ ...props }) => {
  const { userRoleList, userScope, setApiErrorResponse } = useUserContext();

  const [subcontractorCompanies, setSubcontractorCompanies] = useState([]);
  const [createdSubcontractorCompany, setCreatedSubcontractorCompany] =
    useState({});
  const [
    createdSubcontractorCompanyStatus,
    setCreatedSubcontractorCompanyStatus,
  ] = useState(QUERY_STATUS.INITIAL);
  const [editSubcontractorCompanyStatus, setEditSubcontractorCompanyStatus] =
    useState(QUERY_STATUS.INITIAL);
  const [
    deleteSubcontractorCompanyStatus,
    setDeleteSubcontractorCompanyStatus,
  ] = useState(QUERY_STATUS.INITIAL);
  const [
    displaySubcontractorCompanyStatus,
    setDisplaySubcontractorCompanyStatus,
  ] = useState(QUERY_STATUS.INITIAL);
  const [subcontractorCompany, setSubcontractorCompany] = useState({});
  const [subcontractorsExportStatus, setSubcontractorsExportStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [refresh, setRefresh] = useState(false);
  const [
    subcontractingCompanyAccessRights,
    setsubcontractingCompanyAccessRights,
  ] = useState([]);

  const getSubcontractorCompanyBySlug = useCallback(async (slug) => {
    setDisplaySubcontractorCompanyStatus(QUERY_STATUS.LOADING);
    try {
      setsubcontractingCompanyAccessRights([]);
      const result = await getSubcontractorCompanyBySlugQuery({ slug, userScope });
      setDisplaySubcontractorCompanyStatus(QUERY_STATUS.SUCCESS);
      const hasGetAccessRights = result?.access.includes(
        ENTITY_ACCESS_RIGHTS.GET
      );
      if (hasGetAccessRights) {
        setSubcontractorCompany(result);
        setsubcontractingCompanyAccessRights(result?.access);
      }
    } catch (e) {
      if (e.response?.status === HTTP_STATUS_CODE.NOT_FOUND) {
        setDisplaySubcontractorCompanyStatus(QUERY_STATUS.NOT_FOUND);
      } else {
        setDisplaySubcontractorCompanyStatus(QUERY_STATUS.ERROR);
      }
      console.error("getSubcontractorCompanyBySlug", e);
      throw e;
    }
  }, [userScope]);

  const createSubcontractorCompany = useCallback(async (data) => {
    setApiErrorResponse(null);
    setCreatedSubcontractorCompanyStatus(QUERY_STATUS.LOADING);
    try {
      const result = await createSubcontractorCompanyQuery({ data, userScope });
      if (result) {
        setCreatedSubcontractorCompanyStatus(QUERY_STATUS.SUCCESS);
        setCreatedSubcontractorCompany(result);
      }
    } catch (e) {
      setCreatedSubcontractorCompanyStatus(QUERY_STATUS.ERROR);
      const errors = e.response?.data?.["bad-request"];
      if (errors) {
        setApiErrorResponse(extractApiFieldErrorsFlat(errors));
      }
      console.error("subcontractorCompanyCreate", e);
      throw e;
    }
  }, [userScope]);

  const getSubcontractorCompanyById = useCallback(
    async (id) => {
      setDisplaySubcontractorCompanyStatus(QUERY_STATUS.LOADING);
      try {
        setSubcontractorCompany({});
        const result = await getSubcontractorCompanyQuery({ id, userScope });
        setDisplaySubcontractorCompanyStatus(QUERY_STATUS.SUCCESS);
        setSubcontractorCompany(result);
        return result;
      } catch (e) {
        setDisplaySubcontractorCompanyStatus(QUERY_STATUS.ERROR);
        console.error("getSubcontractorCompanyById", e);
        throw e;
      }
    },
    [setSubcontractorCompany, userScope]
  );

  const updateSubcontractorCompany = useCallback(async (id, data) => {
    setApiErrorResponse(null);
    setEditSubcontractorCompanyStatus(QUERY_STATUS.LOADING);
    try {
      const result = await updateSubcontractorCompanyQuery({ id, data, userScope });
      setEditSubcontractorCompanyStatus(QUERY_STATUS.SUCCESS);
      setSubcontractorCompany(result);
      setRefresh(true);
    } catch (e) {
      setEditSubcontractorCompanyStatus(QUERY_STATUS.ERROR);
      const errors = e.response?.data?.["bad-request"];
      if (errors) {
        setApiErrorResponse(extractApiFieldErrorsFlat(errors));
      }
      console.error("updateSubcontractorCompany", e);
      throw e;
    }
  }, [userScope]);

  const searchSubcontractorCompanyByName = useCallback(async (search) => {
    try {
      const result = await searchSubcontractorCompanyByNameQuery({ search, userScope });
      setSubcontractorCompanies((old) =>
        _.uniqWith([...old, ...result], _.isEqual)
      );
    } catch (e) {
      console.error("searchSubcontractorCompanyByName", e);
    }
  }, [userScope]);

  const getSubcontractorCompanyByIdArray = useCallback(async (ids) => {
    try {
      const result = await getSubcontractorCompanyByIdArrayQuery({ ids, userScope });
      setSubcontractorCompanies((old) =>
        _.uniqWith([...old, ...result], _.isEqual)
      );
    } catch (e) {
      console.error("getSubcontractorCompanyByIdArray", e);
    }
  }, [userScope]);

  const deleteSubcontractorCompany = useCallback(async (id) => {
    setDeleteSubcontractorCompanyStatus(QUERY_STATUS.LOADING);
    try {
      await deleteSubcontractorCompanyQuery({ id, userScope });
      setDeleteSubcontractorCompanyStatus(QUERY_STATUS.SUCCESS);
      setSubcontractorCompany({});
    } catch (e) {
      setDeleteSubcontractorCompanyStatus(QUERY_STATUS.ERROR);
      console.error("deleteSubcontractorCompany", e);
      throw e;
    }
  }, [userScope]);

  const getSubcontractorCompaniesByFilters = useCallback(
    async ({ isMultipleIds, filters }) => {
      try {
        if (
          !canUserPerformAction({
            userRoles: userRoleList,
            requiredRoles:
              SUBCONTRACTOR_COMPANY_ROLES.ROLE_GET_SUBCONTRACTOR_COMPANY,
          })
        ) {
          return;
        }
        const result = await getSubcontractorCompaniesByFiltersQuery({
          isMultipleIds,
          filters,
          userScope,
        });
        if (isMultipleIds) setSubcontractorCompanies(result);
        else {
          const hasGetAccessRights = result?.access.includes(
            ENTITY_ACCESS_RIGHTS.GET
          );
          if (hasGetAccessRights) {
            setSubcontractorCompany(result);
            setsubcontractingCompanyAccessRights(result?.access);
          }
        }
      } catch (e) {
        console.error("getSubcontractorCompaniesByFilters", e);
        throw e;
      }
    },
    [userRoleList, userScope]
  );

  const getSubcontractorsListExport = useCallback(
    async ({ company_id, company_name }) => {
      setSubcontractorsExportStatus(QUERY_STATUS.LOADING);
      try {
        const file_id = await generateSubcontractorListCSVQuery({ company_id, userScope });
        await streamingDownload({
          path: `/file/${file_id.uri}`,
          fileName: `${company_name.replace(" ", "_")}_Subcontractor_List.xlsx`,
          mimeType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        setSubcontractorsExportStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setSubcontractorsExportStatus(QUERY_STATUS.ERROR);
        console.error("getSubcontractorsListExport error:", error);
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      deleteSubcontractorCompany,
      createSubcontractorCompany,
      createdSubcontractorCompany,
      createdSubcontractorCompanyStatus,
      getSubcontractorCompanyById,
      setCreatedSubcontractorCompany,
      setCreatedSubcontractorCompanyStatus,
      subcontractorCompany,
      updateSubcontractorCompany,
      editSubcontractorCompanyStatus,
      setEditSubcontractorCompanyStatus,
      subcontractorCompanies,
      searchSubcontractorCompanyByName,
      getSubcontractorCompanyByIdArray,
      setSubcontractorCompany,
      displaySubcontractorCompanyStatus,
      getSubcontractorCompanyBySlug,
      refresh,
      setRefresh,
      subcontractingCompanyAccessRights,
      setsubcontractingCompanyAccessRights,
      getSubcontractorCompaniesByFilters,
      setSubcontractorCompanies,
      deleteSubcontractorCompanyStatus,
      setDeleteSubcontractorCompanyStatus,
      getSubcontractorsListExport,
      subcontractorsExportStatus,
      setSubcontractorsExportStatus,
    }),
    [
      deleteSubcontractorCompany,
      createSubcontractorCompany,
      createdSubcontractorCompany,
      createdSubcontractorCompanyStatus,
      getSubcontractorCompanyById,
      setCreatedSubcontractorCompany,
      setCreatedSubcontractorCompanyStatus,
      subcontractorCompany,
      updateSubcontractorCompany,
      editSubcontractorCompanyStatus,
      setEditSubcontractorCompanyStatus,
      subcontractorCompanies,
      searchSubcontractorCompanyByName,
      getSubcontractorCompanyByIdArray,
      setSubcontractorCompany,
      displaySubcontractorCompanyStatus,
      getSubcontractorCompanyBySlug,
      refresh,
      setRefresh,
      subcontractingCompanyAccessRights,
      setsubcontractingCompanyAccessRights,
      getSubcontractorCompaniesByFilters,
      setSubcontractorCompanies,
      deleteSubcontractorCompanyStatus,
      setDeleteSubcontractorCompanyStatus,
      getSubcontractorsListExport,
      subcontractorsExportStatus,
    ]
  );
  return <SubcontractorCompanyContext.Provider value={values} {...props} />;
};
